import { useClickOutside } from '@mnd-frontend/hooks';
import { Tooltip } from '@mnd-frontend/ui/publish/components';
import { media } from '@mnd-frontend/ui/publish/utils';
import { GREY_200, GREY_400, RED_700, SIZING_1_5 } from '@mnd-frontend/ui/shared/tokens';
import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation, useNavigate } from 'react-router';
import styled, { css } from 'styled-components';
import { track } from '../../../../../lib/tracker';
import { type MainMenuItem, MenuState } from '../types';
import NavigationLink from './NavigationLink';
import SubMenu from './SubNavList';

const linkAndButtonStyles = css`
  color: inherit;
  background-color: transparent;
  border: 0;
  display: grid;
  justify-items: center;
  font-weight: 600;
  font-size: ${SIZING_1_5};
  height: 80px;
  ${media.compactDesktopNavigation()} {
    height: 67px;
  }
  padding: 0;
  gap: 4px;
  i {
    align-self: flex-end;
  }

  &:focus {
    outline: 0;
  }
`;

const NavItem = styled.li<{
  $active?: boolean;
  $topGroup: boolean;
  $itemWidth?: number;
}>`
  background-color: ${({ $active }) => ($active ? GREY_200 : 'transparent')};
  color: ${({ $active }) => ($active ? RED_700 : 'inherit')};
  display: grid;
  place-items: center;

  &:has(:focus-visible) {
    background-color: ${GREY_400};
  }
  &:hover {
    background-color: ${({ $active }) => ($active ? GREY_200 : GREY_400)};
    * {
      outline: 0;
    }
  }

  ${({ $topGroup, $itemWidth }) =>
    !$topGroup &&
    css`
      position: relative;
      width: var(--navigation-large-button-size);
      height: var(--navigation-large-button-size);
      margin-left: calc((var(--mnd-navigation-width) - ${$itemWidth}px) / 2);
      border-radius: 50%;
    `};
  > [data-tooltip-content] {
    width: 100%;
    height: 100%;
  }
  > [data-tooltip-content] > a,
  > a {
    ${linkAndButtonStyles}
  }
`;

const NavItemButton = styled.button<{ $topGroup?: boolean }>`
  ${linkAndButtonStyles}
  ${({ $topGroup }) =>
    !$topGroup &&
    css`
      height: 100%;
      width: 100%;
      place-content: center;
      ${media.compactDesktopNavigation()} {
        height: 100%;
      }
    `}
`;

interface NavigationItemProps {
  topGroup?: boolean;
  itemData: MainMenuItem;
  isSubNavOpen: MenuState | null;
  siteCode?: string;
  setItemHeight?: Dispatch<SetStateAction<number>>;
  subNavOffset?: number;
  close: () => void;
  open: () => void;
}

const isTouchDevice = (() => {
  try {
    return (
      'ontouchstart' in window || // Checks if touch events exist
      navigator.maxTouchPoints > 0 || // Checks if any touch points are registered
      window.matchMedia('(pointer: coarse)').matches // Checks if primary input method is touch
    );
  } catch (_e) {
    return false;
  }
})();

const DEFAULT_NAV_ITEM_WIDTH = 77;

const NavigationItem = ({
  topGroup = false,
  itemData,
  isSubNavOpen,
  siteCode,
  close,
  open,
  setItemHeight,
  subNavOffset,
}: NavigationItemProps) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const navigationItemRef = useRef<HTMLLIElement>(null);
  const [offsetTop, setOffsetTop] = useState<null | number>(null);
  const [navItemWidth, setNavItemWidth] = useState(DEFAULT_NAV_ITEM_WIDTH);

  const location = useLocation();
  const renderNavItemAsLink = itemData.items.length === 1;
  const active = itemData.matchingRoutes
    ? !!itemData.matchingRoutes.find(url => matchPath(url, location.pathname))
    : false;
  const hasSubMenu = itemData.items && itemData.items.length > 1;
  const showSubNav = hasSubMenu && isSubNavOpen === itemData.menuState;
  const showSubNavOnHover = topGroup && itemData.menuState !== MenuState.PressroomSelector;
  const iconToRender =
    topGroup && active ? itemData.iconActive : (itemData.desktopIcon ?? itemData.icon);
  const ariaLabelledby = `nav-item-button-${itemData.menuState}`;
  const ariaControls = `sub-nav-${itemData.menuState}`;
  const isOpen = isSubNavOpen === itemData.menuState;

  const handleOnClick = (item: MainMenuItem) => {
    isSubNavOpen === itemData.menuState ? close() : open();
    if (
      item.menuState === MenuState.Publish ||
      item.menuState === MenuState.Contacts ||
      item.menuState === MenuState.Analyze ||
      item.menuState === MenuState.Monitor
    ) {
      if (!isOpen && isTouchDevice) {
        open();
        return;
      }
      const url = item.items[0].url;
      if (!url) {
        return;
      }
      navigate(url);
    }
  };

  useClickOutside(navigationItemRef, close, isOpen);

  // biome-ignore lint/correctness/useExhaustiveDependencies: To detect the changes of viewport height.
  useLayoutEffect(() => {
    setOffsetTop(navigationItemRef.current?.getBoundingClientRect()?.y || 0);
  }, [isSubNavOpen]);

  useEffect(() => {
    const navItemRect = navigationItemRef.current?.getBoundingClientRect();
    setItemHeight?.(navItemRect?.height || 0);
    setNavItemWidth?.(navItemRect?.width || DEFAULT_NAV_ITEM_WIDTH);
  }, [setItemHeight]);

  const tooltip = !topGroup
    ? itemData.menuState === MenuState.User
      ? t('menu_user_settings')
      : itemData.heading
    : undefined;

  const content = renderNavItemAsLink ? (
    <NavigationLink
      href={itemData.items[0].url}
      onClick={() =>
        track({
          event: 'Navigation link: clicked',
          traits: { menuCategory: itemData.menuState, linkText: itemData.items[0].text },
        })
      }
    >
      {iconToRender}
      {topGroup ? itemData.heading : ''}
    </NavigationLink>
  ) : (
    <NavItemButton
      id={ariaLabelledby}
      $topGroup={topGroup}
      type="button"
      aria-label={`Open ${itemData.heading} menu`}
      aria-expanded={showSubNav}
      aria-controls={ariaControls}
      onClick={() => handleOnClick(itemData)}
    >
      {iconToRender}
      {topGroup ? itemData.heading : ''}
    </NavItemButton>
  );

  return (
    <NavItem
      ref={navigationItemRef}
      $topGroup={topGroup}
      $active={topGroup ? active : false}
      $itemWidth={navItemWidth}
      id={itemData.menuState}
      onMouseOver={showSubNavOnHover && hasSubMenu && !isTouchDevice ? open : undefined}
      onMouseOut={isOpen && showSubNavOnHover && !isTouchDevice ? close : undefined}
    >
      {tooltip ? (
        <Tooltip content={tooltip} placement="right" hidden={showSubNav || isTouchDevice}>
          {content}
        </Tooltip>
      ) : (
        content
      )}
      {!renderNavItemAsLink && offsetTop !== null && (
        <SubMenu
          show={showSubNav}
          subNavOffset={subNavOffset}
          itemData={itemData}
          offsetTop={offsetTop}
          showSubNavOnHover={showSubNavOnHover}
          topGroup={topGroup}
          pressroomSelector={itemData.menuState === MenuState.PressroomSelector}
          ariaId={ariaControls}
          ariaLabelledby={ariaLabelledby}
          siteCode={siteCode}
          close={close}
        />
      )}
    </NavItem>
  );
};

export default NavigationItem;
